import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment-timezone';
import MapView from './MapView';
import useShiftPhotoAction from './useShiftPhotoAction';
import { shownAssignments } from './shownAssignments';

const DEFAULT_TIMEZONE = 'America/Chicago';

// Check-in opens shortly before the shift starts and closes 3 hours after it.
const CHECKIN_OPENS_BEFORE_START_MINUTES = 15;

// Check-out opens shortly before the shift ends and stays open for a while after.
const CHECKOUT_OPENS_BEFORE_END_MINUTES = 15;
const CHECKOUT_CLOSES_AFTER_END_MINUTES = 60;
// In the final minutes of the check-out window a DJ who never checked in may
// still check out, so the day is not left with no record at all.
const CHECKOUT_GRACE_MINUTES = 15;

// How often an open dashboard re-checks its windows, so a DJ who opened it early
// sees Check In or Check Out appear without reloading.
const WINDOW_RECHECK_MS = 30 * 1000;

const CHECK_IN_LABELS = { verb: 'check in', gerund: 'checking in', noun: 'Check-in' };
const CHECK_OUT_LABELS = { verb: 'check out', gerund: 'checking out', noun: 'Check-out' };

// Helper function to check if current time is within the check-in window
// and if today is the election day
export const isWithinCheckInWindow = (startTime, endTime, timezone, shiftDate) => {
  // Parse the shift's start/end time in the provided time zone
  const now = moment().tz(timezone);

  // Get today's date in the shift's timezone
  const today = now.format('YYYY-MM-DD');

  // Parse election date (expected format: MM-DD-YYYY)
  const formattedElectionDate = shiftDate ? moment(shiftDate, 'MM-DD-YYYY').format('YYYY-MM-DD') : null;

  // Check if today is election day
  const isElectionDay = !formattedElectionDate || today === formattedElectionDate;

  // If it's not election day, return false immediately
  if (!isElectionDay) {
    return false;
  }

  // Parse start and end times
  const shiftStart = moment.tz(`${today} ${startTime}`, 'YYYY-MM-DD h:mm a', timezone);

  // Calculate the earliest check-in time (15 minutes before shift start)
  const earliestCheckIn = moment(shiftStart).subtract(CHECKIN_OPENS_BEFORE_START_MINUTES, 'minutes');

  // Calculate the latest check-in time (3 hours after shift start)
  const latestCheckIn = moment(shiftStart).add(3, 'hours');

  // Check if current time is within the check-in window
  return now.isBetween(earliestCheckIn, latestCheckIn, null, '[]');
};

/**
 * Where "now" sits relative to a set's check-out window, evaluated on the shift
 * date in the shift's timezone. `isGrace` is the tail of the window in which a
 * DJ who never checked in may still check out.
 */
export const getCheckOutWindow = (endTime, timezone, shiftDate) => {
  const now = moment().tz(timezone);
  const today = now.format('YYYY-MM-DD');
  const shiftDay = shiftDate ? moment(shiftDate, 'MM-DD-YYYY') : null;
  const isShiftDay = !shiftDay || today === shiftDay.format('YYYY-MM-DD');

  const shiftEnd = moment.tz(`${today} ${endTime}`, 'YYYY-MM-DD h:mm a', timezone);
  const earliest = moment(shiftEnd).subtract(CHECKOUT_OPENS_BEFORE_END_MINUTES, 'minutes');
  const latest = moment(shiftEnd).add(CHECKOUT_CLOSES_AFTER_END_MINUTES, 'minutes');
  const graceStart = moment(latest).subtract(CHECKOUT_GRACE_MINUTES, 'minutes');

  const isOpen = isShiftDay && now.isBetween(earliest, latest, null, '[]');

  return {
    isOpen,
    isGrace: isOpen && now.isSameOrAfter(graceStart),
    earliestTime: earliest.format('h:mm a'),
    latestTime: latest.format('h:mm a'),
    formattedShiftDate: shiftDay ? shiftDay.format('MMMM D, YYYY') : 'Election Day',
  };
};

/* ---------- Small presentational pieces shared by the check-in and check-out rows ---------- */

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
  </svg>
);

const WarningIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

const Spinner = () => (
  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

// The primary action button, with its in-flight state. When it can't be used
// (e.g. the photo is in a format the AI review can't read) it turns grey.
const ActionButton = ({ label, loadingLabel, isLoading, disabled, onClick }) => {
  let state = 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm';
  if (isLoading) state = 'bg-blue-600 text-white shadow-sm opacity-75 cursor-not-allowed';
  else if (disabled) state = 'bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed';
  return (
    <button
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`flex items-center justify-center w-full sm:w-auto px-4 py-2 border border-transparent rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${state}`}
    >
      {isLoading ? (
        <>
          <Spinner />
          {loadingLabel}
        </>
      ) : (
        <>
          <CheckCircleIcon className="w-5 h-5 mr-2" />
          {label}
        </>
      )}
    </button>
  );
};

// Amber notice shown while an action's window is closed.
const WindowNotice = ({ children }) => (
  <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200 mt-2 sm:max-w-lg">
    <div className="flex items-start">
      <WarningIcon className="w-5 h-5 mr-1.5 flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  </div>
);

const DonePill = ({ label }) => (
  <div className="flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-lg">
    <CheckIcon className="w-5 h-5 mr-2" />
    <span className="font-medium">{label}</span>
  </div>
);

const SuccessLine = ({ children }) => (
  <div className="mt-2 text-sm text-green-600 font-medium flex items-center">
    <CheckIcon className="w-5 h-5 mr-1.5" />
    {children}
  </div>
);

const ErrorLine = ({ children }) => (
  <div className="mt-2 text-sm text-red-600 font-medium flex items-center">
    <WarningIcon className="w-5 h-5 mr-1.5" />
    {children}
  </div>
);

// What one action has to report for a set: success, the captured position, errors.
const ActionStatus = ({ setId, action, successMessage }) => (
  <>
    {action.success[setId] && <SuccessLine>{successMessage}</SuccessLine>}
    {action.locationData[setId] && (
      <div className="mt-3 map-container" style={{ position: 'relative', zIndex: 1 }}>
        <MapView latitude={action.locationData[setId].latitude} longitude={action.locationData[setId].longitude} />
      </div>
    )}
    {action.imageError[setId] && <ErrorLine>{action.imageError[setId]}</ErrorLine>}
  </>
);

// Upload / Take Photo buttons, or the preview of the chosen photo.
const PhotoPicker = ({ setId, action, canPick, previewAlt }) => {
  if (action.images[setId]) {
    return (
      <div className="mb-3 relative w-full sm:w-auto">
        <div className="relative">
          <img
            src={action.images[setId].url}
            alt={previewAlt}
            className="w-full sm:w-32 h-32 object-contain rounded-lg border border-gray-200"
          />
          <button
            onClick={() => action.removeImage(setId)}
            className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200 focus:outline-none"
            aria-label="Remove image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1 text-center">Photo ready</p>
      </div>
    );
  }

  if (!canPick) return null;

  const buttonClass =
    'flex items-center justify-center w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors';

  return (
    <div className="flex flex-col sm:flex-row gap-2 mb-2 w-full">
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => action.handleFileSelect(setId, e)}
        ref={action.registerFileInput(setId, 'gallery')}
      />
      <input
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => action.handleFileSelect(setId, e)}
        ref={action.registerFileInput(setId, 'camera')}
      />

      <button onClick={() => action.triggerFileInput(setId, 'gallery')} className={buttonClass}>
        <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        Upload Photo
      </button>

      <button onClick={() => action.triggerFileInput(setId, 'camera')} className={buttonClass}>
        <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 014 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0118.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Take Photo
      </button>
    </div>
  );
};

// Location trouble: a warning, plus a way to proceed without coordinates.
const LocationFallback = ({ message, canSkip, skipLabel, onSkip, disabled }) => (
  <div
    className={`mt-4 text-sm font-medium flex flex-col ${
      canSkip ? 'text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200' : 'text-red-600'
    }`}
  >
    <div className="flex items-start">
      <WarningIcon className="w-5 h-5 mr-1.5 flex-shrink-0 mt-0.5" />
      <span>{message}</span>
    </div>
    {canSkip && (
      <button
        onClick={onSkip}
        disabled={disabled}
        className="mt-3 ml-auto px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-md transition-colors duration-200 text-sm disabled:opacity-75 disabled:cursor-not-allowed"
      >
        {skipLabel}
      </button>
    )}
  </div>
);

/* ---------- Per-kind action rows ---------- */

// ShiftCheckIn component
const ShiftCheckIn = ({ shift, onCheckIn, isCheckedIn, isLoading, disabled, onCheckInAvailabilityChange, clock }) => {
  const [canCheckIn, setCanCheckIn] = useState(false);
  const [timeInfo, setTimeInfo] = useState({
    earliestTime: '',
    latestTime: '',
    currentTime: '',
    formattedElectionDate: ''
  });

  // Extract the necessary values from shift to use as dependencies
  const shiftId = shift?.id;
  const shiftStartTime = shift?.shift_start_time;
  const shiftEndTime = shift?.shift_end_time;
  const shiftTimezone = shift?.shift_timezone;
  const shiftDate = shift?.shift_date;
  useEffect(() => {
    // Skip if already checked in
    if (isCheckedIn) return;

    // Skip if missing required data
    if (!shiftStartTime || !shiftEndTime || !shiftTimezone || !shiftDate) return;

    // Check if current time is within check-in window
    const checkInAllowed = isWithinCheckInWindow(shiftStartTime, shiftEndTime, shiftTimezone, shiftDate);
    setCanCheckIn(checkInAllowed);

    // Notify parent component about check-in availability
    if (onCheckInAvailabilityChange && shiftId) {
      onCheckInAvailabilityChange(shiftId, checkInAllowed);
    }

    // For debugging/display purposes
    const now = moment().tz(shiftTimezone);
    const today = now.format('YYYY-MM-DD');
    const shiftStart = moment.tz(`${today} ${shiftStartTime}`, 'YYYY-MM-DD h:mm a', shiftTimezone);
    const earliestCheckIn = moment(shiftStart).subtract(CHECKIN_OPENS_BEFORE_START_MINUTES, 'minutes');
    const latestCheckIn = moment(shiftStart).add(3, 'hours');

    // Format the election date for display (MM-DD-YYYY to Month DD, YYYY)
    const formattedDate = shiftDate ? moment(shiftDate, 'MM-DD-YYYY').format('MMMM D, YYYY') : 'Election Day';

    setTimeInfo({
      earliestTime: earliestCheckIn.format('h:mm a'),
      latestTime: latestCheckIn.format('h:mm a'),
      currentTime: now.format('h:mm a'),
      formattedElectionDate: formattedDate
    });
  }, [shiftId, shiftStartTime, shiftEndTime, shiftTimezone, shiftDate, isCheckedIn, onCheckInAvailabilityChange, clock]);

  if (isCheckedIn) return null;

  return (
    <div className="mt-2">
      {canCheckIn ? (
        <ActionButton
          label="Check In"
          loadingLabel="Checking In..."
          isLoading={isLoading}
          disabled={disabled}
          onClick={onCheckIn}
        />
      ) : (
        <WindowNotice>
          You cannot check in at this time. Check-in for this set is available from {timeInfo.formattedElectionDate} -{' '}
          {timeInfo.earliestTime} to {timeInfo.latestTime}.
        </WindowNotice>
      )}
    </div>
  );
};

// The check-out button or, while its window is closed, when it opens.
const ShiftCheckOut = ({ checkoutWindow, onCheckOut, isLoading, disabled }) => (
  <div className="mt-2">
    {checkoutWindow.isOpen ? (
      <ActionButton
        label="Check Out"
        loadingLabel="Checking Out..."
        isLoading={isLoading}
        disabled={disabled}
        onClick={onCheckOut}
      />
    ) : (
      <WindowNotice>
        You cannot check out at this time. Check-out for this set is available from {checkoutWindow.formattedShiftDate} -{' '}
        {checkoutWindow.earliestTime} to {checkoutWindow.latestTime}.
      </WindowNotice>
    )}
  </div>
);

/* ---------- Widget ---------- */

const EDayCheckIn = ({ elections, setElections }) => {
  // State for sets (shifts)
  const [sets, setSets] = useState([]);
  // Firebase Storage folder for this election's photos (Elections "Folder Path").
  const [uploadFolder, setUploadFolder] = useState('');
  // Which action asked for the location modal: { kind: 'checkin' | 'checkout', setId }
  const [locationPrompt, setLocationPrompt] = useState(null);
  // { [setId]: 'checkin' | 'checkout' } while a save is in flight. Shared by both
  // actions so they cannot overlap on one set (see useShiftPhotoAction).
  const [inFlight, setInFlight] = useState({});
  // Ticks every WINDOW_RECHECK_MS. Window state is computed when the elections
  // arrive, so without this a dashboard left open stays on whatever was true then.
  const [clock, setClock] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), WINDOW_RECHECK_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Same dates ShiftCheckIn uses: shift_date, or election_date when it is blank.
    setSets((prevSets) =>
      prevSets.map((set) => ({
        ...set,
        canCheckIn: isWithinCheckInWindow(
          set.shift_start_time,
          set.shift_end_time,
          set.shift_timezone,
          set.shift_date || set.assignment.election_date
        ),
        checkoutWindow: getCheckOutWindow(set.shift_end_time, set.shift_timezone, set.shift_date),
      }))
    );
  }, [clock]);

  // Process election data to create sets
  useEffect(() => {
    if (elections && elections.length > 0) {
      const activeElection = elections.find(election =>
        election.active &&
        election.dj_elections &&
        election.dj_elections.length > 0 &&
        shownAssignments(election.dj_elections[0].active_assignments).length > 0 &&
        !election.dj_elections[0].dont_display_assignments
      );

      if (activeElection) {
        const assignments = shownAssignments(activeElection.dj_elections[0].active_assignments);
        setUploadFolder(activeElection.folder_path || '');

        // Transform assignments into sets format
        const newSets = assignments.map(assignment => {
          const timezone = assignment.shift_timezone || DEFAULT_TIMEZONE;
          return {
            id: assignment.rid,
            // The record as the API returned it. Every save spreads it under the
            // changed fields so Quickbase is never sent a partial record.
            assignment,
            timeSlot: assignment.shift,
            timeRange: `${assignment.shift_start_time} - ${assignment.shift_end_time}`,
            pollingPlace: `Polling Location`,
            address: assignment.polling_place_address,
            isCheckedIn: assignment.shift_checkin || false,
            isCheckedOut: assignment.shift_checkout || false,
            canCheckIn: isWithinCheckInWindow(
              assignment.shift_start_time,
              assignment.shift_end_time,
              timezone,
              assignment.election_date
            ),
            checkoutWindow: getCheckOutWindow(assignment.shift_end_time, timezone, assignment.shift_date),
            shift_start_time: assignment.shift_start_time,
            shift_end_time: assignment.shift_end_time,
            shift_timezone: timezone,
            shift_date: assignment.shift_date,
            document_url: assignment.document_url || '' // Store document URL for permission waiver
          };
        });

        setSets(newSets);
      } else {
        // No active assignments, set empty array
        setSets([]);
        setUploadFolder('');
      }
    }
  }, [elections]);

  // Memoize the check-in availability callback to prevent infinite re-renders
  const handleCheckInAvailabilityChange = useCallback((setId, available) => {
    // Update the local state to reflect the check-in availability
    setSets((prevSets) =>
      prevSets.map((item) =>
        item.id === setId ? { ...item, canCheckIn: available } : item
      )
    );
  }, []);

  const getAssignment = (setId) => {
    const set = sets.find((item) => item.id === setId);
    return set ? set.assignment : undefined;
  };

  // Fold saved fields back into this widget's copy of the record and into the
  // Dashboard's elections state, so the next action in this session sends them
  // forward and nothing else re-renders from stale data.
  const applyAssignmentChanges = (setId, changes) => {
    setSets((prevSets) =>
      prevSets.map((item) => {
        if (item.id !== setId) return item;
        const assignment = { ...item.assignment, ...changes };
        return {
          ...item,
          assignment,
          isCheckedIn: !!assignment.shift_checkin,
          isCheckedOut: !!assignment.shift_checkout,
        };
      })
    );

    if (setElections) {
      setElections((prevElections) =>
        prevElections.map((election) => {
          if (!election.dj_elections) return election;
          return {
            ...election,
            dj_elections: election.dj_elections.map((djElection) => {
              if (!djElection.active_assignments) return djElection;
              return {
                ...djElection,
                active_assignments: djElection.active_assignments.map((assignment) =>
                  assignment.rid === setId ? { ...assignment, ...changes } : assignment
                ),
              };
            }),
          };
        })
      );
    }
  };

  const checkIn = useShiftPhotoAction({
    kind: 'checkin',
    labels: CHECK_IN_LABELS,
    inFlight,
    setInFlight,
    getAssignment,
    buildChanges: ({ imageUrl, coords }) => ({
      shift_checkin: true,
      check_in_timestamp: new Date().toISOString(),
      users_latitude: coords ? coords.latitude : '',
      users_longitude: coords ? coords.longitude : '',
      checkin_img: imageUrl,
      dj_confirmation_shift: true,
    }),
    onSaved: applyAssignmentChanges,
    requestLocationPrompt: (setId) => setLocationPrompt({ kind: 'checkin', setId }),
    uploadFolder,
  });

  const checkOut = useShiftPhotoAction({
    kind: 'checkout',
    labels: CHECK_OUT_LABELS,
    inFlight,
    setInFlight,
    getAssignment,
    buildChanges: ({ imageUrl, coords }) => ({
      shift_checkout: true,
      check_out_timestamp: new Date().toISOString(),
      checkout_latitude: coords ? coords.latitude : '',
      checkout_longitude: coords ? coords.longitude : '',
      checkout_img: imageUrl,
    }),
    onSaved: applyAssignmentChanges,
    requestLocationPrompt: (setId) => setLocationPrompt({ kind: 'checkout', setId }),
    uploadFolder,
  });

  // Cancel modal
  const cancelLocationAccess = () => {
    setLocationPrompt(null);
  };

  // Continue from modal
  const continueFromModal = () => {
    if (!locationPrompt) return;
    const { kind, setId } = locationPrompt;
    setLocationPrompt(null);
    (kind === 'checkout' ? checkOut : checkIn).proceed(setId);
  };

  // The check-out row is for sets that checked in, sets already checked out,
  // and, in the grace period, sets that never checked in.
  const showsCheckOut = (set) => set.isCheckedOut || set.isCheckedIn || set.checkoutWindow.isGrace;

  return (
    <>
      {sets.length > 0 ? (
        <div className="bg-white rounded-xl shadow-xl overflow-hidden mb-6">
          <div className="p-6">
            {/* Location Prompt Modal */}
            {locationPrompt && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
                <div className="bg-white rounded-lg shadow-lg p-6 w-80">
                  <h2 className="text-lg font-semibold mb-4">Share Your Location</h2>
                  <p className="text-sm text-gray-700 mb-4">
                    We need to access your location to confirm your{' '}
                    {locationPrompt.kind === 'checkout' ? 'check-out' : 'check-in'}. Please allow location sharing in
                    your browser settings.
                  </p>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={cancelLocationAccess}
                      className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={continueFromModal}
                      className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Check Into Your Set</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Select your assigned polling location set below to check in
                </p>
              </div>
              <div className="hidden sm:block">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Election Day
                </span>
              </div>
            </div>

            {/* Sets List */}
            <div className="space-y-4">
              {sets.map((set) => (
                <div
                  key={set.id}
                  className={`border rounded-lg p-4 transition-all duration-200 ${
                    set.isCheckedIn
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  {/* Check-in row */}
                  <div className="flex flex-col sm:flex-row justify-between">
                    <div className="mb-4 sm:mb-0">
                      <div className="flex items-center mb-2">
                        <span className={`px-3 py-1 rounded-md text-base font-medium shadow-sm ${
                          set.timeSlot === 'Morning'
                            ? 'bg-amber-100 text-amber-800'
                            : set.timeSlot === 'Afternoon'
                              ? 'bg-sky-100 text-sky-800'
                              : 'bg-purple-100 text-purple-800'
                        }`}>
                          {set.timeSlot} Set • {set.timeRange}
                        </span>
                      </div>
                      <h3 className="text-base font-medium text-gray-800 mb-1">
                        {set.pollingPlace}
                      </h3>
                      <p className="text-sm text-gray-500">{set.address}</p>

                      {/* Permission Waiver Document Link */}
                      {set.document_url && (
                        <div className="mt-2">
                          <a
                            href={set.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors"
                          >
                            <svg
                              className="w-4 h-4 mr-1.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            Access permission document
                          </a>
                        </div>
                      )}

                      <ActionStatus setId={set.id} action={checkIn} successMessage="Successfully checked in!" />
                    </div>

                    <div className="flex flex-col items-end space-y-3">
                      {set.isCheckedIn ? (
                        <DonePill label="Checked In" />
                      ) : (
                        <>
                          <PhotoPicker
                            setId={set.id}
                            action={checkIn}
                            canPick={set.canCheckIn}
                            previewAlt="Check-in preview"
                          />
                          <ShiftCheckIn
                            shift={set}
                            onCheckIn={() => checkIn.request(set.id)}
                            isCheckedIn={set.isCheckedIn}
                            isLoading={checkIn.isLoading(set.id)}
                            disabled={checkIn.isBusy(set.id)}
                            onCheckInAvailabilityChange={handleCheckInAvailabilityChange}
                            clock={clock}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {checkIn.locationError[set.id] && !set.isCheckedIn && (
                    <LocationFallback
                      message={checkIn.locationError[set.id]}
                      canSkip={checkIn.skipLocation[set.id]}
                      skipLabel="Yes, Still Check In"
                      onSkip={() => checkIn.skipLocationAndProceed(set.id)}
                      disabled={checkIn.isBusy(set.id)}
                    />
                  )}

                  {/* Check-out row */}
                  {showsCheckOut(set) && (
                    <div className={`mt-4 pt-4 border-t ${set.isCheckedIn ? 'border-green-200' : 'border-gray-200'}`}>
                      <div className="flex flex-col sm:flex-row justify-between">
                        <div className="mb-4 sm:mb-0">
                          <h4 className="text-base font-medium text-gray-800 mb-1">Check out of this set</h4>
                          {!set.isCheckedOut && (
                            <p className="text-sm text-gray-500">Take a photo before you leave.</p>
                          )}

                          <ActionStatus setId={set.id} action={checkOut} successMessage="Successfully checked out!" />
                        </div>

                        <div className="flex flex-col items-end space-y-3">
                          {set.isCheckedOut ? (
                            <DonePill label="Checked Out" />
                          ) : (
                            <>
                              <PhotoPicker
                                setId={set.id}
                                action={checkOut}
                                canPick={set.checkoutWindow.isOpen}
                                previewAlt="Check-out preview"
                              />
                              <ShiftCheckOut
                                checkoutWindow={set.checkoutWindow}
                                onCheckOut={() => checkOut.request(set.id)}
                                isLoading={checkOut.isLoading(set.id)}
                                disabled={checkOut.isBusy(set.id)}
                              />
                            </>
                          )}
                        </div>
                      </div>

                      {checkOut.locationError[set.id] && !set.isCheckedOut && (
                        <LocationFallback
                          message={checkOut.locationError[set.id]}
                          canSkip={checkOut.skipLocation[set.id]}
                          skipLabel="Yes, Still Check Out"
                          onSkip={() => checkOut.skipLocationAndProceed(set.id)}
                          disabled={checkOut.isBusy(set.id)}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Help Text */}
            <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-gray-800">
                    Need help with your set?
                  </h3>
                  <div className="mt-2 text-sm text-gray-600">
                    <p>
                      If you are having issues checking in or run into any issues
                      during your set, contact{' '}
                      <a
                        href="mailto:djs@djsatthepolls.org"
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        djs@djsatthepolls.org
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default EDayCheckIn;
